import { createContext, useCallback, useContext, useState } from "react";
import { DateTime } from "luxon";

type NotificationType = "success" | "error" | "info";

type NotificationEntry = {
  id: string;
  timestamp: DateTime;
  type: NotificationType;
  content: React.ReactNode;
};

let counter = 0;

function useValue() {
  const [entries, setEntries] = useState<NotificationEntry[]>([]);

  const emit = useCallback(
    (content: React.ReactNode, type: NotificationType) => {
      const entry: NotificationEntry = {
        id: (++counter).toString(),
        timestamp: DateTime.now(),
        type,
        content,
      };

      setEntries((entries) => {
        return [...entries, entry];
      });
    },
    []
  );

  const clear = useCallback(() => {
    setEntries([]);
  }, []);

  return { entries, emit, clear };
}

const Context = createContext<ReturnType<typeof useValue> | null>(null);

interface NotificationContextProps {
  children: React.ReactNode;
}

export default function NotificationContext({
  children,
}: NotificationContextProps) {
  const value = useValue();

  return <Context.Provider value={value}>{children}</Context.Provider>;
}

export function useNotifications() {
  const context = useContext(Context);
  if (!context) {
    throw new Error("useNotifications without NotificationContext");
  }
  return context;
}
